import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa6";
import { Chapter } from "@shared_types";
import { haptic } from "@/lib/haptic";
import ChapterCard, { PathConnector } from "./ChapterCard";
import CourseCompletedPopup from "./CourseCompletedPopup";
import ExploreOtherCoursesCard from "./ExploreOtherCoursesCard";
import {
  ChapterCompletionId,
  chapterCompletionIdsMatch,
  clearCompletedChapterSignal,
  peekCompletedChapterSignal,
} from "./completedChapterSignal";

/**
 * Web port of the iOS ChapterPathList — the rich card list that replaces the old
 * winding-node CoursePath on the Home/Learn tab.
 *
 * Source of truth: client-native/src/features/home/components/ChapterPathList.tsx:24-242
 * (completed/remaining split, ">5 completed" collapse pill, "Explore other
 * courses" card that activates once the course is complete).
 *
 * Chapter-completion celebration (iOS useChapterCelebration.ts + HomeScreen.tsx:395-413):
 * returning home after finishing a chapter consumes the completedChapterSignal,
 * bounces the completed card, then either pulses a glow ring on the next chapter
 * or — when the whole course is done — shows CourseCompletedPopup. The held-card
 * scroll-snap choreography is simplified to a scrollIntoView; timings
 * (WAIT_BEFORE_ANIMATE_MS / COLLAPSE_AFTER_MS) match iOS ChapterCard.CELEBRATION.
 * Flight School signals are left for the FS home path (iOS shows that popup
 * straight from the signal, no card celebration).
 */

// iOS ChapterCard.tsx:22-25.
const CELEBRATION = {
  WAIT_BEFORE_ANIMATE_MS: 300,
  COLLAPSE_AFTER_MS: 2500,
};

// iOS ChapterCard.tsx glowRing (:258-272) — 2px blue ring, blue glow shadow,
// inset -4 around the 16px-radius card; opacity pulses 0→1→0 at 1200ms each way.
const GLOW_RING_STYLE: React.CSSProperties = {
  position: "absolute",
  left: -4,
  right: -4,
  bottom: -4,
  height: 128, // card (120) + 4px overhang top/bottom; connector above stays clear
  borderRadius: 20,
  border: "2px solid var(--color-blue)",
  boxShadow: "0 0 12px rgba(12, 146, 241, 0.5)",
  pointerEvents: "none",
  opacity: 0,
  animation: "parrot-glow-pulse 2400ms ease-in-out infinite",
  zIndex: 1,
};

const ChapterPathList: React.FC<{
  chapters: Chapter[];
  courseTitle: string;
}> = ({ chapters, courseTitle }) => {
  const navigate = useNavigate();
  const [completedCollapsed, setCompletedCollapsed] = useState(true);
  const currentNodeRef = useRef<HTMLDivElement | null>(null);

  // iOS onLockedPress (HomeScreen.tsx:103-108,326): a warning haptic + snap to
  // the current chapter node.
  const handleLockedPress = useCallback(() => {
    haptic.warning();
    currentNodeRef.current?.scrollIntoView({ block: "center" });
  }, []);

  const [glowChapterId, setGlowChapterId] = useState<ChapterCompletionId | null>(
    null,
  );
  const [bounceChapterId, setBounceChapterId] =
    useState<ChapterCompletionId | null>(null);
  const [courseCompletedPopup, setCourseCompletedPopup] = useState<{
    courseName: string;
    isFlightSchool: boolean;
  } | null>(null);
  const celebratedNodeRef = useRef<HTMLDivElement | null>(null);
  const pendingSignal = useRef<ReturnType<typeof peekCompletedChapterSignal>>(null);
  const celebrationDone = useRef(false);

  // Consume the completion signal once chapter data confirms the completion
  // (iOS useChapterCelebration focus effect + data-arrival fallback). The
  // consumed signal is held in a ref so the finishing timer can be rescheduled
  // across effect re-runs (fresh `chapters` identity per parent render) and
  // StrictMode's dev unmount/remount without losing the celebration.
  useEffect(() => {
    if (celebrationDone.current) return;

    if (!pendingSignal.current) {
      const signal = peekCompletedChapterSignal();
      // FS graduation popup is owned by the Flight School home path, not this list.
      if (!signal || signal.isFlightSchool) return;

      const target = chapters.find((ch) =>
        chapterCompletionIdsMatch(ch.chapterId, signal.chapterId),
      );
      if (!target || target.status !== "completed") return;

      pendingSignal.current = signal;
      clearCompletedChapterSignal();
      setBounceChapterId(signal.chapterId);
    }

    // iOS: snap the completed card into view, bounce after WAIT_BEFORE_ANIMATE_MS,
    // then COLLAPSE_AFTER_MS later show the popup / glow the next chapter.
    const signal = pendingSignal.current;
    const timer = setTimeout(() => {
      celebrationDone.current = true;
      setBounceChapterId(null);
      if (signal.courseCompleted) {
        setCourseCompletedPopup({ courseName: courseTitle, isFlightSchool: false });
      } else {
        const nextChapter = chapters.find((ch) => ch.status !== "completed");
        if (nextChapter) setGlowChapterId(nextChapter.chapterId);
      }
    }, CELEBRATION.WAIT_BEFORE_ANIMATE_MS + CELEBRATION.COLLAPSE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [chapters, courseTitle]);

  // Snap the celebrated card into view once its node renders (iOS
  // snapToChapterNode targets ~35% of the viewport; "center" approximates it).
  useEffect(() => {
    if (bounceChapterId == null) return;
    celebratedNodeRef.current?.scrollIntoView({ block: "center" });
  }, [bounceChapterId]);

  const completedChapters = chapters.filter((ch) => ch.status === "completed");
  const remainingChapters = chapters.filter((ch) => ch.status !== "completed");
  const allCompleted = chapters.length > 0 && remainingChapters.length === 0;
  const shouldCollapse = completedChapters.length > 5;
  const collapsed = shouldCollapse && completedCollapsed;

  // Wraps a card so the celebration ring/bounce can decorate it without
  // touching ChapterCard itself (iOS passes glowPulse/animateCompletion props;
  // the ring metrics here mirror its glowRing style block).
  const renderCard = (ch: Chapter) => {
    const isGlowing = chapterCompletionIdsMatch(ch.chapterId, glowChapterId);
    const isBouncing = chapterCompletionIdsMatch(ch.chapterId, bounceChapterId);
    const isCurrent =
      ch.status === "current" || ch.status === "in_progress";
    return (
      <div
        key={ch.chapterId}
        ref={
          isBouncing
            ? celebratedNodeRef
            : isCurrent
              ? currentNodeRef
              : undefined
        }
        style={{
          position: "relative",
          animation: isBouncing
            ? // iOS bounceY: -10px over 150ms, settle over 250ms, after the
              // 300ms WAIT_BEFORE_ANIMATE_MS hold.
              "parrot-chapter-bounce 400ms ease-out 300ms"
            : undefined,
        }}
      >
        {isGlowing && <div aria-hidden style={GLOW_RING_STYLE} />}
        <ChapterCard ch={ch} onLockedPress={handleLockedPress} />
      </div>
    );
  };

  return (
    // No course-title heading here: iOS's ChapterPathList renders the cards
    // directly (the course name already lives in the CURRENT COURSE hero above).
    // The old <Title text={courseTitle}/> duplicated "Beginner 1" on Home.
    <div className="flex flex-col w-full justify-start gap-4 no-scrollbar">
      <style>{`
        @keyframes parrot-glow-pulse { 0% { opacity: 0; } 50% { opacity: 1; } 100% { opacity: 0; } }
        @keyframes parrot-chapter-bounce { 0% { transform: translateY(0); } 37% { transform: translateY(-10px); } 100% { transform: translateY(0); } }
      `}</style>
      <div className="flex flex-col w-full pb-24">
        {/* Collapsed pill OR all completed cards */}
        {collapsed ? (
          <>
            <PathConnector />
            <button
              type="button"
              onClick={() => setCompletedCollapsed(false)}
              className="flex flex-row items-center self-center gap-1.5 px-4 py-2.5 my-1 rounded-full bg-[var(--color-grey-lightest)] active:opacity-70"
            >
              <FaCheck className="w-4 h-4 text-[var(--color-green)]" />
              <span className="text-sm font-semibold text-[var(--color-grey)]">
                Show {completedChapters.length} completed chapters
              </span>
              <FaChevronDown className="w-3.5 h-3.5 text-[var(--color-grey-other)]" />
            </button>
          </>
        ) : (
          completedChapters.map(renderCard)
        )}

        {/* Hide button when expanded */}
        {shouldCollapse && !completedCollapsed && (
          <>
            <PathConnector />
            <button
              type="button"
              onClick={() => setCompletedCollapsed(true)}
              className="flex flex-row items-center self-center gap-1.5 px-4 py-2.5 my-1 rounded-full bg-[var(--color-grey-lightest)] active:opacity-70"
            >
              <FaChevronUp className="w-3.5 h-3.5 text-[var(--color-grey-other)]" />
              <span className="text-sm font-semibold text-[var(--color-grey)]">
                Hide completed
              </span>
            </button>
          </>
        )}

        {/* Remaining (current + locked) cards */}
        {remainingChapters.map(renderCard)}

        {/* Explore other courses */}
        <PathConnector />
        <ExploreOtherCoursesCard completed={allCompleted} />
      </div>

      {/* iOS HomeScreen.tsx:395-413 — "Pick next course" routes to the courses
          tab; the FS → course-5 hand-off lives with the FS home path. The
          app-rating eligibility check stays in ModuleComplete on web (no
          App Store prompt exists in the PWA). */}
      <CourseCompletedPopup
        visible={!!courseCompletedPopup}
        courseName={courseCompletedPopup?.courseName || ""}
        isFlightSchool={courseCompletedPopup?.isFlightSchool || false}
        onPickNextCourse={() => {
          setCourseCompletedPopup(null);
          navigate("/home/courses");
        }}
        onDismiss={() => {
          if (!courseCompletedPopup?.isFlightSchool) {
            setCourseCompletedPopup(null);
          }
        }}
      />
    </div>
  );
};

export default ChapterPathList;
